import type { ProjectWithRelations } from "@/hooks/useProjects";

export const UPCOMING_PROJECT_LIMIT = 5;

function toUtcDay(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.getTime();
}

export function getUpcomingProjectSortDate(project: ProjectWithRelations) {
  return (
    project.planning_start_date ??
    project.event_start_date ??
    project.start_date ??
    project.planning_end_date ??
    project.event_end_date ??
    project.end_date ??
    null
  );
}

export function isUpcomingProject(
  project: Pick<
    ProjectWithRelations,
    | "planning_start_date"
    | "event_start_date"
    | "start_date"
    | "planning_end_date"
    | "event_end_date"
    | "end_date"
  >,
  todayUtcMs: number,
) {
  const start =
    toUtcDay(project.planning_start_date) ??
    toUtcDay(project.event_start_date) ??
    toUtcDay(project.start_date);
  const end =
    toUtcDay(project.planning_end_date) ??
    toUtcDay(project.event_end_date) ??
    toUtcDay(project.end_date) ??
    start;

  if (start === null && end === null) return false;
  return (end ?? start!) >= todayUtcMs;
}

type DatedProject = Parameters<typeof isUpcomingProject>[0];

/**
 * Splits projects into upcoming (running or in the future, soonest first)
 * and past (most recent first). Projects without any date count as upcoming
 * and are listed after the dated ones.
 */
export function splitUpcomingAndPastProjects<T extends DatedProject>(
  projects: T[],
  todayUtcMs: number,
): { upcoming: T[]; past: T[] } {
  const sortDate = (project: T) =>
    getUpcomingProjectSortDate(project as unknown as ProjectWithRelations);
  const upcoming: T[] = [];
  const past: T[] = [];

  for (const project of projects) {
    if (sortDate(project) === null || isUpcomingProject(project, todayUtcMs)) {
      upcoming.push(project);
    } else {
      past.push(project);
    }
  }

  upcoming.sort((a, b) => {
    const aDate = sortDate(a);
    const bDate = sortDate(b);
    if (aDate === null || bDate === null) {
      return (aDate === null ? 1 : 0) - (bDate === null ? 1 : 0);
    }
    return aDate.localeCompare(bDate);
  });
  past.sort((a, b) => (sortDate(b) ?? "").localeCompare(sortDate(a) ?? ""));

  return { upcoming, past };
}

/** Today at 00:00 UTC, as used by isUpcomingProject. */
export function todayUtcMs(now = new Date()) {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}
